import { useEffect, useState } from 'react';
import { MessageSquare, Pencil, X } from 'lucide-react';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';

export type CartLineNoteMode = 'comment' | 'rename';

interface Props {
  mode: CartLineNoteMode;
  itemName: string;
  initialValue?: string;
  onClose: () => void;
  onSave: (value: string) => void;
}

/** Per-line rename or packing note — invoice/quote only (does not change product master). */
export default function CartLineNoteModal({
  mode,
  itemName,
  initialValue = '',
  onClose,
  onSave,
}: Props) {
  useEscapeToClose(onClose);
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue, mode, itemName]);

  const isRename = mode === 'rename';
  const title = isRename ? 'Rename for this invoice' : 'Add line comment';
  const hint = isRename
    ? 'Shown on this sale/quote only. The product catalogue name is unchanged.'
    : 'e.g. only chilled ones — pack carefully. Shown on receipt for this line.';
  const placeholder = isRename ? 'Display name on this invoice' : 'Comment for kitchen / packing…';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = value.trim();
    if (isRename && !next) return;
    onSave(next);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center shrink-0">
              {isRename ? <Pencil className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
              <p className="text-xs text-slate-500 truncate">{itemName}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <p className="text-xs text-slate-500">{hint}</p>
          {isRename ? (
            <input
              autoFocus
              className="input-field w-full"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              maxLength={140}
            />
          ) : (
            <textarea
              autoFocus
              className="input-field w-full min-h-[96px] resize-y"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              maxLength={500}
            />
          )}
          <div className="flex justify-end gap-2 pt-1">
            {!isRename && value.trim() && (
              <button
                type="button"
                onClick={() => {
                  onSave('');
                  onClose();
                }}
                className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Clear
              </button>
            )}
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isRename && !value.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
